/**
 * ConfluencePanel — markets where multiple agents independently agree.
 * Renders the real /api/agents/confluence wire shape (signal_confluence rows):
 * market_id, direction, agent_ids, n_agents, avg_confidence, avg_edge, detected_at.
 */

import { useAgentConfluence } from "@/sdk/hooks/use-agents";

function formatPct(value: number | null | undefined): string {
  return value != null ? `${(Number(value) * 100).toFixed(0)}%` : "—";
}

export function ConfluencePanel() {
  const { data, isLoading, isError } = useAgentConfluence(20);
  const entries = data?.confluence ?? [];

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-zinc-500">
        Loading confluence...
      </div>
    );
  }

  if (entries.length === 0) {
    // An errored read is not an empty book — say so instead of faking calm.
    if (isError) {
      return (
        <div className="flex h-full items-center justify-center text-xs text-amber-400">
          Confluence data unavailable — backend unreachable
        </div>
      );
    }
    return (
      <div className="flex h-full items-center justify-center text-xs text-zinc-600">
        No confluence detected yet
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-zinc-950">
      <div className="flex-1 overflow-auto">
        {entries.map((entry) => {
          const agentIds = Array.isArray(entry.agent_ids) ? entry.agent_ids : [];
          const direction = (entry.direction ?? "").toUpperCase();
          return (
            <div
              // market_id alone is not unique — the same market can recur
              // over time and per direction.
              key={`${entry.market_id}:${entry.direction}:${entry.detected_at}`}
              className="border-b border-zinc-800/50 px-3 py-2"
            >
              <div className="mb-1.5 flex items-center justify-between">
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className={`shrink-0 text-[10px] font-bold uppercase ${
                      direction === "BUY"
                        ? "text-emerald-400"
                        : direction === "SELL"
                          ? "text-red-400"
                          : "text-zinc-400"
                    }`}
                  >
                    {direction || "—"}
                  </span>
                  <p className="truncate text-xs text-zinc-300">{entry.market_id}</p>
                </div>
                <span className="shrink-0 rounded bg-indigo-950 px-1.5 py-0.5 text-[10px] font-bold text-indigo-400">
                  {entry.n_agents} agents
                </span>
              </div>

              <div className="mb-1.5 flex items-center gap-3 text-[10px] text-zinc-500">
                <span>
                  Conf: <span className="font-mono text-zinc-300">{formatPct(entry.avg_confidence)}</span>
                </span>
                <span>
                  Edge: <span className="font-mono text-zinc-300">{formatPct(entry.avg_edge)}</span>
                </span>
                <span className="ml-auto text-zinc-600">
                  {entry.detected_at ? new Date(entry.detected_at).toLocaleTimeString() : "—"}
                </span>
              </div>

              {/* Agent chips */}
              <div className="flex flex-wrap gap-1">
                {agentIds.map((id) => (
                  <span
                    key={id}
                    className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-300"
                  >
                    {id}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
